import { useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { podeEditarItens, totaisDoPlano, type StatusDoPlano } from "@bookdental/schemas";
import { trpc } from "@/lib/trpc";
import { cn } from "@/lib/cn";
import { useConfirm } from "@/lib/confirm";
import { usePermissao } from "@/lib/use-permissao";
import { abrirImpressao } from "@/components/production/impressao";
import { segurarDocumentoNaEstacao } from "@/lib/estacao-de-etiquetas";
import { useFluxoNovoPedido } from "@/components/prosthesis/FluxoNovoPedido";
import { ProsthesisOrderDetailDialog } from "@/components/prosthesis/ProsthesisOrderDetailDialog";
import { CabecalhoDoPlano } from "./CabecalhoDoPlano";
import { DadosDoPlano } from "./DadosDoPlano";
import { DesenhoDoPlano, colunasDoEditor } from "./DesenhoDoPlano";
import { FormularioDoItem } from "./FormularioDoItem";
import { ItensDoPlano, type ItemDoPlanoTela } from "./ItensDoPlano";
import { TotaisDoPlano } from "./TotaisDoPlano";
import { CSS_DO_ORCAMENTO, htmlDoOrcamento } from "./orcamento";
import { useModoDoDesenho } from "./use-modo-do-desenho";
import { useMontagem } from "./use-montagem";
import { usePlanoMutations } from "./use-plano";

/**
 * Um plano, no desenho do pedido de prótese: a arcada (ou o rosto, no modo Face) à esquerda, o "Adicionar
 * procedimento" no meio (enquanto dá pra editar) e o plano à direita. Plano novo (`planoId` nulo)
 * só vira registro no primeiro procedimento — quem abre e desiste não deixa rascunho vazio.
 */
export function EditorDoPlano({
  planoId,
  patientId,
  dentistaInicial,
  nascimento,
  sexo,
  onVoltar,
}: {
  planoId: string | null;
  patientId: string;
  dentistaInicial: string | null;
  /** Data de nascimento do paciente — criança abre a arcada decídua. */
  nascimento?: Date | string | null;
  /** Sexo do paciente — escolhe o modelo do desenho da face. */
  sexo?: string | null;
  onVoltar: () => void;
}) {
  const utils = trpc.useUtils();
  const confirm = useConfirm();
  const m = usePlanoMutations();
  const [id, setId] = useState(planoId);
  const query = trpc.plano.obter.useQuery({ id: id ?? "" }, { enabled: !!id });
  const podeMontar = usePermissao("treatments.manage");
  const podePedirProtese = usePermissao("prosthesis.manage");
  const fluxo = useFluxoNovoPedido({ onConcluido: () => utils.plano.invalidate() });
  const montagem = useMontagem();
  const [pedidoAberto, setPedidoAberto] = useState<string | null>(null);
  const [dentistaLocal, setDentistaLocal] = useState(dentistaInicial);
  const [destaqueHover, setDestaqueHover] = useState<string | null>(null);
  const [destaqueFixo, setDestaqueFixo] = useState<string | null>(null);
  const destacado = destaqueHover ?? destaqueFixo;
  const itens = useMemo(() => (query.data?.itens ?? []) as ItemDoPlanoTela[], [query.data]);
  const desenho = useModoDoDesenho(query.data, sexo, id ? (modeloDaFace) => m.atualizar.mutate({ id, modeloDaFace }) : null);

  if (id && (query.isLoading || !query.data)) {
    return (
      <div className="flex justify-center py-12">
        <Loader2 className="h-6 w-6 animate-spin text-brand-600" />
      </div>
    );
  }
  const plano = query.data;
  const status = (plano?.status ?? "RASCUNHO") as StatusDoPlano;
  const dentistaId = plano ? plano.dentistaId : dentistaLocal;
  const editavel = podeEditarItens(status) && podeMontar;
  const totais = plano ?? totaisDoPlano([]);

  const garantirPlano = async () => {
    if (id) return id;
    const novo = await m.criar.mutateAsync({ patientId, dentistaId: dentistaLocal });
    setId(novo.id);
    return novo.id;
  };
  const imprimir = async (forcar = false) => {
    if (!id) return;
    if (!forcar && segurarDocumentoNaEstacao(() => void imprimir(true))) return;
    // Janela aberta no clique: depois do await o navegador trataria como pop-up e bloquearia.
    const janela = abrirImpressao(900, 1000);
    if (!janela) return toast.error("Libere as janelas pop-up para imprimir.");
    try {
      const dados = await utils.plano.orcamento.fetch({ id });
      janela.escrever(`Orçamento nº ${dados.numero}`, CSS_DO_ORCAMENTO, htmlDoOrcamento(dados));
      if (!plano?.entregueEm) m.marcarEntregue.mutate({ id });
    } catch {
      janela.fechar();
      toast.error("Não foi possível gerar o orçamento.");
    }
  };
  const mudarStatus = async (s: StatusDoPlano) => {
    if (!id) return;
    const perguntas: Partial<Record<StatusDoPlano, string>> = { CANCELADO: "Cancelar o plano?", RECUSADO: "Marcar como recusado pelo paciente?" };
    if (perguntas[s] && !(await confirm({ title: perguntas[s]!, description: "Ele fica no histórico do paciente.", confirmText: "Confirmar", cancelText: "Voltar" }))) return;
    m.mudarStatus.mutate({ id, status: s });
  };
  const mover = (item: ItemDoPlanoTela, direcao: -1 | 1) => {
    if (!id) return;
    const ids = itens.map((i) => i.id);
    const de = ids.indexOf(item.id);
    [ids[de], ids[de + direcao]] = [ids[de + direcao], ids[de]];
    m.reordenar.mutate({ planoId: id, ids });
  };

  return (
    <div className="space-y-5">
      <CabecalhoDoPlano
        numero={plano?.numero ?? null}
        titulo={plano?.titulo ?? null}
        criadoEm={plano?.criadoEm ?? null}
        status={status}
        entregueEm={plano?.entregueEm ?? null}
        dentistaId={dentistaId}
        podeMontar={podeMontar}
        temItens={itens.length > 0}
        onVoltar={onVoltar}
        onImprimir={() => imprimir()}
        onStatus={mudarStatus}
        onDentista={(d) => (id ? m.atualizar.mutate({ id, dentistaId: d }) : setDentistaLocal(d))}
      />
      <div className={cn("grid gap-5", colunasDoEditor(desenho.modo, editavel))}>
        <DesenhoDoPlano
          modo={desenho.modo}
          onModo={desenho.setModo}
          itens={itens}
          destacadoId={destacado}
          montagem={montagem}
          editavel={editavel}
          nascimento={nascimento}
          modelo={desenho.modelo}
          onModelo={desenho.mudarModelo}
          onFixarDestaque={(itemId) => setDestaqueFixo((atual) => (atual === itemId ? null : itemId))}
          onLimparDestaque={() => setDestaqueFixo(null)}
        />
        {editavel && (
          <FormularioDoItem
            key={`${montagem.editando?.id ?? "novo"}-${montagem.rodada}`}
            garantirPlano={garantirPlano}
            item={montagem.editando}
            dentistaId={dentistaId}
            dentes={montagem.dentes}
            quantidadeDeDentes={montagem.quantidadeDeDentes}
            dentesTravados={montagem.fdi.length > 0}
            modoFace={desenho.modo === "FACE"}
            marcacoes={montagem.marcacoes}
            unidade={montagem.unidade}
            onDentesManuais={montagem.setManuais}
            onLimparDentes={montagem.limparDentes}
            onLimparMarcacoes={() => montagem.setMarcacoes([])}
            onProcedimento={(p) => {
              montagem.escolherProcedimento(p);
              desenho.seguirProcedimento(p);
            }}
            onConcluido={montagem.limpar}
          />
        )}
        <div className={cn("space-y-4", editavel && "lg:col-span-2 xl:col-span-1")}>
          <ItensDoPlano
            itens={itens}
            editandoId={montagem.editando?.id ?? null}
            destacadoId={destacado}
            editavel={editavel}
            aprovado={status === "APROVADO"}
            podeMontar={podeMontar}
            // Pedido de prótese só depois que o paciente aprova — antes disso é orçamento.
            podePedirProtese={podePedirProtese && (status === "APROVADO" || status === "CONCLUIDO")}
            onDestacar={setDestaqueHover}
            onFixarDestaque={(itemId) => {
              const item = itens.find((i) => i.id === itemId);
              if (item) desenho.seguirItem(item);
              setDestaqueFixo((atual) => (atual === itemId ? null : itemId));
            }}
            onEditar={(i) => {
              desenho.seguirItem(i);
              montagem.editar(i);
            }}
            onRemover={async (i) => {
              if (await confirm({ title: "Remover procedimento?", description: i.nome, confirmText: "Remover", cancelText: "Voltar" })) m.removerItem.mutate({ id: i.id });
            }}
            onMover={mover}
            onMarcarFeito={(i, feito) => m.marcarFeito.mutate({ itemId: i.id, feito })}
            onPedirProtese={(i) => plano && fluxo.abrirDoPlano({ patient: plano.patient, itemId: i.id, dentes: i.dentes })}
            onAbrirPedido={setPedidoAberto}
          />
          <TotaisDoPlano
            subtotal={totais.subtotal}
            desconto={totais.desconto}
            total={totais.total}
            semPreco={totais.semPreco}
            editavel={editavel && !!id}
            onDesconto={(descontoCents) => id && m.atualizar.mutate({ id, descontoCents })}
          />
          {id && plano && (
            <DadosDoPlano
              titulo={plano.titulo}
              validadeAte={plano.validadeAte}
              observacoes={plano.observacoes}
              podeMontar={podeMontar}
              onAtualizar={(dados) => m.atualizar.mutate({ id, ...dados })}
            />
          )}
        </div>
      </div>
      {fluxo.elementos}
      <ProsthesisOrderDetailDialog orderId={pedidoAberto} onClose={() => setPedidoAberto(null)} readOnly />
    </div>
  );
}
